import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { IBfxUserSettingsPageSubComponent } from "app/bfx-user-settings/bfx-user-settings-page.component";
import { ProfileUpdateRequestImpl } from "shared/cubiq-model-multi-asset";
import { BFXAppNotificationType } from "shared/cubiq-services";
import { BfxUserSettingsValidityState } from "./bfx-user-settings-page.component";
import { BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import { BfxPopupEventSetting } from "./model/bfx-popup-event-setting";

import { BfxNotificationSettingsComponent } from "app/bfx-user-settings/bfx-notification-settings.component";
import { BfxUserSettingsComponent } from "app/bfx-user-settings/bfx-user-settings.component";

import { BfxNotificationSettingRowComponent } from "app/bfx-user-settings/bfx-notification-setting-row.component";
import { BfxAppEventSetting } from "app/bfx-user-settings/model/bfx-app-event-setting";
import { BfxUserNotificationSettingsService } from "app/bfx-user-settings/services/bfx-user-notification-settings.service";
import { BFXNotificationType } from "../../shared/cubiq-services/model/core/notifications/BFXNotificationType";

@Component({
  selector: "bfx-notification-settings-unsupported",
  templateUrl: "./html/bfx-notification-settings-order.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export abstract class BfxNotificationSettingsBaseComponent
  implements IBfxUserSettingsPageSubComponent<BfxNotificationSettingsComponent>
{
  /**********************************************************************/
  /* DATA */

  public categoryComponent: BfxNotificationSettingsComponent = null;
  public parentComponent: BfxUserSettingsComponent = null;
  public dataChanged: boolean = false;
  private popupRowTitleKey: string = "user_settings.notifications.popup_notifications";
  private appRowTitleKey: string = "user_settings.notifications.app_notifications";
  public popupRowTitle: string = "";
  public appRowTitle: string = "";

  @Input() public audioDisabled: boolean = false;

  @Output() public audioChanged: EventEmitter<boolean> = new EventEmitter();
  public popupSettings: Array<BfxPopupEventSetting> = [];
  public applicationSettings: Array<BfxAppEventSetting> = [];

  @ViewChildren(BfxNotificationSettingRowComponent)
  private settingComponents: QueryList<BfxNotificationSettingRowComponent>;

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    public translate: TranslateService,
    private settingsService: BfxUserNotificationSettingsService,

    private cd: ChangeDetectorRef,
  ) {
    // this.nativeDesktop = NativeDesktopSupport.isNativeDesktopContainer();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  public initialize(): Promise<void[]> {
    this.initializeLabelTranslations();

    return this.initializeSettings();
  }

  protected initializeSettings(): Promise<void[]> {
    return Promise.all([this.initializePopupSettings(), this.initializeApplicationSettings()]);
  }

  protected initializePopupSettings(): Promise<void> {
    return this.settingsService
      .getPopupNotificationSettings(this.isSupportedPopupType.bind(this))
      .then((popupSettings) => {
        this.popupSettings = [...popupSettings];
        this.cd.markForCheck();
      });
  }

  protected initializeApplicationSettings(): Promise<void> {
    return this.settingsService
      .getAppNotificationSettings(this.isSupportedAppType.bind(this))
      .then((appSettings) => {
        this.applicationSettings = [...appSettings];
        this.cd.markForCheck();
      });
  }

  protected abstract isSupportedPopupType(type: BFXNotificationType): boolean;
  protected abstract isSupportedAppType(type: BFXAppNotificationType): boolean;

  private initializeLabelTranslations(): void {
    this.popupRowTitle = this.translate.instant(this.popupRowTitleKey);
    this.appRowTitle = this.translate.instant(this.appRowTitleKey);
  }

  /**********************************************************************/
  /* SAVE SETTINGS TO PROFILE */

  public postSave(request: ProfileUpdateRequestImpl): void {
    this.dataChanged = false;

    this.cd.markForCheck();
  }

  public save(_request: ProfileUpdateRequestImpl): void {}

  public postReset(): void {
    this.dataChanged = false;

    this.cd.markForCheck();
  }

  public reset(): Promise<void> {
    return this.initializeSettings().then(() => {
      this.cd.detectChanges();
    });
  }

  public toggleAudio() {
    this.audioDisabled = !this.audioDisabled;

    this.audioChanged.emit(this.audioDisabled);
    this.cd.markForCheck();
  }

  public onTabActivated(event: any): void {}

  public validate(): Promise<BfxUserSettingsValidityState> {
    let state: BfxUserSettingsValidityState;
    if (this.settingComponents.some((component) => !component.validate())) {
      state = BfxUserSettingsValidityState.INVALID;
    } else {
      state = BfxUserSettingsValidityState.VALID;
    }
    return Promise.resolve(state);
  }

  public settingChanged(setting: BfxPopupEventSetting, event) {
    const settingValue = Object.assign({}, setting, {
      enabled: event.enabled,
      audio: event.audio,
      filter: event.filter,
    });
    logger.out.info("ns-limit : Popup setting changed :" + settingValue.toString());
    this.settingsService.updatePopupSetting(settingValue);
    this.pageDataChanged();
  }

  public appSettingChanged(setting: BfxAppEventSetting, event) {
    const settingValue = Object.assign({}, setting, {
      enabled: event.enabled,
      audio: event.audio,
      filter: event.filter,
      returnOnLogin: event.returnOnLogin,
    });
    logger.out.info("ns-limit : App setting changed :" + settingValue.toString());
    this.settingsService.updateAppSetting(settingValue);
    this.pageDataChanged();
  }

  public pageDataChanged() {
    this.dataChanged = true;
    this.cd.detectChanges();
    if (this.parentComponent) {
      this.parentComponent.validate(this.categoryComponent);
    }
  }

  public validChanged(setting: any, event) {
    logger.out.info("ns-limit : Setting validity changed :" + event?.toString());
    this.pageDataChanged();
  }

  public hasDataChanged(): boolean {
    return this.dataChanged;
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {}

  public pageScrolled(_event) {
    // logger.out.info("Notification page scrolled");
    this.settingComponents?.forEach((row) => row.closeDropdowns());
  }
}
